<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { slide } from "svelte/transition"
  import { SUBSCRIPTION_ROLES_LIST } from "$app/constants"
  import { appStore } from "$app/store.svelte"
  import { signupStore } from "$app/signupstore.svelte"
  import {
    xhr,
    toast,
    formatter,
    dateConverter,
    catchError,
    convertObjectToUrl
  } from "$app/utils"
  import Card from "$app/Pages/Signup/Card.svelte"
  import SaveButton from "$app/Pages/Signup/SaveButton.svelte"
  import ShowCard from "../Billing/ShowCard.svelte"
  import { Loader2 } from "lucide-svelte"
  import Role from "./Role.svelte"
  import Permission from "./Permission.svelte"

  let {
    oncancel,
    oncomplete
  }: {
    oncancel: () => void
    oncomplete: () => void
  } = $props()

  let card = $state<Card>(null)
  let saving: boolean = $state(false)
  let addCard = $state(false)
  let stripe_invoice: any = $state({})

  signupStore.subscription.billing_interval =
    appStore.subscription.billing_interval

  signupStore.selectedPlan = signupStore.products.find(
    (p) => p.id === "standard"
  )
  const fullAccessIdx = signupStore.member_roles.findIndex(
    (m) => m.id === "full_access"
  )
  let fullAccessRole = signupStore.member_roles[fullAccessIdx]
  const teamMemberIdx = signupStore.member_roles.findIndex(
    (m) => m.id === "team_member"
  )
  let teamMemberRole = signupStore.member_roles[teamMemberIdx]
  const recognitionMemberIdx = signupStore.member_roles.findIndex(
    (m) => m.id === "recognition_member"
  )
  let recognitionMemberRole = signupStore.member_roles[recognitionMemberIdx]

  let prevUsers = $state(null)
  let users = $state(null)

  let updateError = $state("")
  let isLoading = $state(true)
  async function initialize(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get<{
        users: UserType[]
        roles_hash: any
        alignment_only_members: any
        stripe_data: StripeDataType
        stripe_upcoming_invoice: any
      }>(`/plans/${appStore.plan.id}/admin/billing/member_data`)

      let data = resp.data

      users = Object.values(data.users)
      prevUsers = Object.values(data.users)

      //appStore.user_roles = data.roles_hash
      //appStore.alignment_only_members = data.alignment_only_members

      //stripe_data = data.stripe_data
      //signupStore.stripe_data = data.stripe_data
      //stripe_upcoming_invoice = data.stripe_upcoming_invoice
    } catch (e) {
      console.log(e)
      toast("There was an error loading the member data", "error")
    } finally {
      isLoading = false

      signupStore.setSignupDataToSubscription()
    }
  }

  let updating = $state(false)
  let hasUpdated = $state(false)
  async function calculateTotal(): Promise<void> {
    updating = true

    if (JSON.stringify(users) == JSON.stringify(appStore.users)) {
      hasUpdated = false
      updating = false
      return
    }

    const data = {
      full_access_total: fullAccessRole.count,
      team_member_total: teamMemberRole.count,
      recognition_member_total: recognitionMemberRole.count,
      subscription_type: "standard",
      billing_interval: signupStore.billingInterval,
      coupon: null
    }
    if (signupStore.coupon.valid) {
      data.coupon = signupStore.coupon.id
    }

    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/admin/billing/calculate_update?${convertObjectToUrl(data)}`
      )

      updateError = ""
      stripe_invoice = resp.data.invoice
      //console.log("Stripe Invoice: ", stripe_invoice)
    } catch (e: any) {
      console.error(e)
      updateError =
        "We are sorry, there was a problem when calculating totals. Please contact support@leadwithpurpose.com for assistance."
      toast("There was an error calculating new totals", "error")
    } finally {
      hasUpdated = true
      updating = false
    }
  }

  async function getData(): Promise<any> {
    const auth_token_input = <HTMLInputElement>(
      document.getElementById("x-csrf-token")
    )
    const auth_token = auth_token_input?.value || ""

    let cardToken = ""
    if (addCard) {
      cardToken = await card.getStripeToken()
    }

    if (addCard && cardToken === "") {
      toast(
        "There was an error with the credit card verification. Please try again.",
        "error"
      )
      saving = false
      return
    }

    return {
      subscription: {
        stripe_token: cardToken,
        stripe_coupon: signupStore.coupon.id,
        billing_interval: signupStore.billingInterval,
        discount: signupStore.discount,
        subscription_type: "standard",
        name: signupStore.selectedPlan.name,
        full_access_total: fullAccessRole.count,
        team_member_total: teamMemberRole.count,
        recognition_member_total: recognitionMemberRole.count,
        auth_token: auth_token
      },
      users: users
    }
  }

  /**
   * Submit data
   */
  async function handleSubmit(): Promise<void> {
    saving = true
    const data = await getData()

    const [error, resp] = await catchError(
      xhr.post<{
        plan: PlanType
        subscription: SubscriptionType
        users: [UserType]
      }>(`/plans/${appStore.plan.id}/admin/billing/update_standard`, {
        ...data
      })
    )

    if (error) {
      toast(
        "Error updating Standard Subscription. Please try again or contact support@leadwithpurpose.com",
        "error",
        { duration: 30000 }
      )
      return
    }
    saving = false

    toast(
      "Congratulations! You have successfully updated your Plan 🎉",
      "success"
    )

    appStore.plan = resp.data.plan
    signupStore.subscription = resp.data.subscription
    appStore.subscription = resp.data.subscription
    users = appStore.users
    appStore.users = users

    oncomplete()
  }

  function updateRole(user) {
    let prevUser = prevUsers.find((pu) => pu.id == user.id)

    if (["admin", "leader", "manager"].includes(prevUser.role)) {
      fullAccessRole.count--
    }
    if (prevUser.role === "member") {
      teamMemberRole.count--
    }
    if (prevUser.role === "recognition") {
      recognitionMemberRole.count--
    }
    if (["admin", "leader", "manager"].includes(user.role)) {
      fullAccessRole.count++
    }
    if (user.role === "member") {
      teamMemberRole.count++
    }
    if (user.role === "recognition") {
      recognitionMemberRole.count++
    }

    prevUser.role = user.role

    calculateTotal()
  }

  function userPriceRole(role: string): string {
    if (role === "admin" || role === "leader" || role === "manager") {
      return signupStore.priceRole("full_access").toFixed(2)
    } else if (role == "member") {
      return signupStore.priceRole("team_member").toFixed(2)
    } else if (role == "recognition") {
      return signupStore.priceRole("recognition_member").toFixed(2)
    }
  }

  onMount(() => {
    initialize()
  })
</script>

<div class="py-2 px-2">
  <div class="flex justify-between">
    <h3 class="text-xl text-black">Migrate Member Roles</h3>
    <button
      class="text-base font-semibold py-0.5 px-2 border border-gray-200 hover:bg-gray-50 hover:text-gray-600 rounded-sm hover:shadow"
      onclick={() => {
        oncancel()
      }}>Cancel Role Migration</button
    >
  </div>
  <div>
    <div class="mt-4 text-base text-gray-700">
      {#if signupStore.stripe_data.subscription?.status === "trialing"}
        <p>
          You are currently in your trial period, which will end on
          <span class="font-bold"
            >{dateConverter(
              signupStore.stripe_data.subscription.trial_end
            )}</span
          >. Any changes you make today will affect your initial payment once
          your trial period is completed.
        </p>
      {:else}
        You may modify your member roles and adjust their access levels. You
        will be billed immediately for any changes prorated until the end of
        your current billing cycle.
      {/if}
    </div>
  </div>
</div>

{#if isLoading}
  <div class="py-12 flex justify-center w-full">
    <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
  </div>
{:else}
  <div transition:slide class="px-4 shadow-md border rounded-md py-4 mt-2">
    <div class="grid grid-cols-12 w-full py-2 border-b border-gray-200">
      <div class="col-span-5">Member</div>
      <div class="col-span-5 flex justify-start">Role</div>
      <div class="col-span-2 flex justify-end">Cost</div>
    </div>

    {#each users as user, index (user.id)}
      <div class="grid grid-cols-12 w-full py-2 gap-x-2 w-full">
        <div class="col-span-5 flex justify-start items-center">
          <div class="mr-2 w-8">
            <img
              src={user.avatar_url}
              class="w-8 h-8 rounded-full"
              alt={user.name}
            />
          </div>
          <div class="overflow-hidden">
            <p class="text-base text-gray-800">
              {user.name}
              {#if user.id == appStore.current_user.id}
                <span
                  class="text-xs text-gray-500 py-0.5 px-1 bg-orange-100 rounded"
                >
                  You
                </span>
              {/if}
            </p>
            <p class="text-xs text-gray-700">
              {user.email}
            </p>
          </div>
        </div>

        <div class="col-span-5 flex items-center">
          {#if user.id == appStore.current_user.id}
            <Role {user} />
            <div class="ml-2"><Permission {user} /></div>
          {:else}
            <select
              bind:value={users[index].role}
              onchange={() => updateRole(user)}
            >
              {#each SUBSCRIPTION_ROLES_LIST as role}
                <option value={role.id}>{role.label}</option>
              {/each}
            </select>
          {/if}
        </div>
        <div class="col-span-2 flex items-center justify-end">
          <p class="text-xs text-gray-700">
            ${userPriceRole(users[index].role)} / {appStore.subscription
              .billing_interval}
          </p>
        </div>
      </div>
    {/each}
    {#if updating}
      <div class="py-12 flex justify-center w-full">
        <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
      </div>
    {:else if hasUpdated && updateError.length === 0}
      <div class="px-2 md:px-4">
        <div class="px-12 py-4 max-w-2xl mx-auto shadow border rounded mt-4">
          <div class="grid grid-cols-3 gap-2 mt-4">
            {#each signupStore.member_roles as role (role.id)}
              <div class="col-span-2 flex items-center justify-start gap-1">
                <div class="text-sm text-gray-500">
                  {role.count}
                  {role.title}{role.count === 1 ? "" : "s"}
                </div>
              </div>
              <div class="font-semibold text-sm text-right">
                <div>
                  ${signupStore.priceRoleTotal(role.id).toFixed(2)} / {appStore
                    .subscription.billing_interval}
                </div>
              </div>
            {/each}

            <div class="col-span-3 border-t border-gray-400"></div>
            {#if signupStore.stripe_data.subscription?.status === "trialing"}
              <div class="col-span-2 text-lg font-bold">
                Subscription Cost After Changes
              </div>
              <div class="font-bold text-lg text-right">
                {#if updating}
                  <Loader2 class="animate-spin" />
                {:else}
                  {formatter.format(stripe_invoice.total / 100)} / {appStore
                    .subscription.billing_interval}
                {/if}
              </div>
            {:else}
              <div class="col-span-2 text-lg font-bold">
                {#if stripe_invoice.total > 0}
                  Payment Today for Changes
                {:else}
                  Credit Applied for Changes
                {/if}
              </div>
              <div class="font-bold text-lg text-right">
                {#if updating}
                  <Loader2 class="animate-spin" />
                {:else}
                  {formatter.format(stripe_invoice.total / 100)}
                {/if}
              </div>
            {/if}
          </div>
        </div>
        <div class="px-12 max-w-2xl mx-auto">
          <div class="px-2 py-4 md:px-4 flex items-center justify-center">
            <div class="flex items-center">
              <h3 class="text-xl py-3 font-semibold text-tertiary flex-1">
                You will pay with:
              </h3>
            </div>
            <ShowCard
              stripe_payment_method={signupStore.stripe_data.payment_method}
            />
          </div>
        </div>
        <div class="max-w-2xl mx-auto">
          <SaveButton
            bind:saving
            title="Adjust Member Roles"
            onclick={handleSubmit}
          />
        </div>
      </div>
    {:else if updateError.length > 0}
      <div
        class="py-4 flex justify-center w-full border-t border-gray-200 mt-4"
      >
        <span class="text-red-700 px-4 py-2 text-sm">{updateError}</span>
      </div>
    {:else}
      <div
        class="py-4 flex justify-center w-full border-t border-gray-200 mt-4"
      >
        <span class="text-gray-500 px-4 py-2 text-sm"
          >No changes to member roles...</span
        >
      </div>
    {/if}
  </div>
{/if}
